/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ProjectConfigurationStatus.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomDataGridV2 from "../../../common/datagrid/CustomDataGridV2";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../../../config/appTheme";
import {
  dashboardAction,
  HandleApiActions,
} from "../../../../../redux/actions";

function ProjectConfigurationStatus() {
  // hooks
  const dispatch = useDispatch();
  const selectedProject = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedProjectId = selectedProject?.project_id;
  // States
  const [statusDetails, setStatusDetails] = useState([]);
  const [recentActivitiesDetails, setRecentActivitiesDetails] = useState([]);

  const getDefaultValue = (arr) => (arr && arr.length > 0 ? arr[0] : 0);
  const intr = getDefaultValue(statusDetails?.intr_details);
  const deps = getDefaultValue(statusDetails?.dependencies_details);
  const linkModuleType = { SWM: 1, EE: 2 };

  // Functions
  const getStatusDetails = async (projectId) => {
    let apiDetails = dashboardAction.get_dash_board_status;
    delete apiDetails.action_type;

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
        },
        show_toast: false,
      })
    );
    if (resp.error) {
      setStatusDetails([]);
    } else {
      setStatusDetails(resp.data);
    }
  };

  const getRecentActivitiesDetails = async (projectId) => {
    let apiDetails = dashboardAction.get_dash_board_activities;
    delete apiDetails.action_type;

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        params: {
          project_id: projectId,
        },
        show_toast: false,
      })
    );
    if (resp.error) {
      setRecentActivitiesDetails({});
    } else {
      setRecentActivitiesDetails(resp.data);
    }
  };

  //   UseEffect
  useEffect(() => {
    if (selectedProject?.project_id) {
      getRecentActivitiesDetails(Number(selectedProject?.project_id));
      getStatusDetails(Number(selectedProject?.project_id));
    }
  }, [selectedProject]);

  const ROWS = [
    {
      id: 1,
      type: "Intersections",
      linked: intr?.linked || 0,
      unlinked: intr?.un_linked || 0,
    },
    {
      id: 2,
      type: "Dependencies",
      linked: deps?.linked || 0,
      unlinked: deps?.un_linked || 0,
    },
  ];
  const COLUMNS = [
    {
      field: "type",
      headerName: "Type",
      flex: 1,
    },
    {
      field: "linked",
      headerName: "Linked",
      flex: 1,
      renderCell: (params) => {
        const { row } = params;
        const isIntersection = row.type === "Intersections";
        const count = params.value;

        if (count > 0) {
          const link = isIntersection
            ? // `/dashboard/type-approval?project_id=${selectedProjectId}&setTabValue=${2}`
            `/dashboard/v2/type-approval/tickets`
            : // `/dashboard/labcar?project_id=${selectedProjectId}&linkSoftwareModulesScreen=true&linkModuleType=${linkModuleType?.SWM}`
            `/dashboard/v2/link`;
          return (
            <Link to={link} style={{ textDecoration: "none" }}>
              <Typography sx={{ fontSize: "12px" }}>{count}</Typography>
            </Link>
          );
        }
        return (
          <Typography sx={{ fontSize: "12px", color: "gray" }}>0</Typography>
        );
      },
    },
    {
      field: "unlinked",
      headerName: "Unlinked",
      flex: 1,
      renderCell: (params) => {
        const { row } = params;
        const isIntersection = row.type === "Intersections";
        const count = params.value;

        if (count > 0) {
          const link = isIntersection
            ? // `/dashboard/type-approval?project_id=${selectedProjectId}`
            `/dashboard/v2/type-approval/link`
            : // `/dashboard/labcar?project_id=${selectedProjectId}&linkSoftwareModulesScreen=true&linkModuleType=${linkModuleType?.SWM}`
            `/dashboard/v2/link`;
          return (
            <Link to={link} style={{ textDecoration: "none" }}>
              <Typography sx={{ fontSize: "12px" }}>{count}</Typography>
            </Link>
          );
        }
        return (
          <Typography sx={{ fontSize: "12px", color: "gray" }}>0</Typography>
        );
      },
    },
  ];
  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ textAlign: "left" }}>
        <Typography sx={componentStyle.font.tabLevelHeader}>
          Components Linkage
        </Typography>
      </Box>
      <CustomDataGridV2 columns={COLUMNS} rows={ROWS} />
    </Box>
  );
}

export default ProjectConfigurationStatus;
